import { creditCardType } from 'card-validator';
import get from 'lodash-es/get';
import PaymentApiClient from '../../api/clients/PaymentApiClient';
import RoomBookingApiClient from '../../api/clients/RoomBookingApiClient';
import Shift4i4GoApiClient from '../../api/clients/Shift4i4GoApiClient';
import { isLocationAvailableWithAffirm } from '../../api/Utils/affirmUtils';
import { isPayPalEnabled } from '../../api/Utils/paypalUtils';
import { determineWhichFlow } from '../../flows/DynamicFlow/utilities/helpers';
import { isArrayEmpty } from '../../utilities/arraysUtils';
import { ASSETS_LOCATIONS, SITE_NIAGON, SUPPORTED_COUNTRIES, getAssetsUrl } from '../../utilities/constants';
import { isDevEnvironment } from '../../utilities/env';
import { maskPhone } from '../../utilities/maskUtils';
import { checkObjectProperties } from '../../utilities/objectKeysManagementUtils';
import { GwDatesWrapper } from '../_internal_date_/gwDatesWrapper';
import unmaskValue from '../utilities/unmaskValue';
import createTiogasReservationSchema from './createTiogasReservationSchema';

import { FLOW_NAMES } from '../../flows/DynamicFlow/utilities/constants';
import routes from '../../routes';
import { handlePackagesTotalWithTax } from '../Summary/components/SimpleSummary/utilities/helperFunctions';
import { PAYMENT_FORM_IMAGES } from './assets/images';
import * as COPY from './utilities/copy';
import partyReservationSchema from './utilities/createPartyReservationSchema';
import { paymentMethodsTypes } from './utilities/helpers';

/**
 * Type of payment methods
 * @enum {string}
 */
export const paymentMethods = {
  CREDIT_CARD: 1,
  AFFIRM: 3,
  PAYPAL: 4,
  SHIFT4: 5,
  APPLE_PAY: 5,
  GOOGLE_PAY: 5
};

export const flowOrigins = {
  ROOM_BOOKING: 0,
  DAY_PASS: 1
};

const paymentFormLogos = {
  creditCard: getAssetsUrl(ASSETS_LOCATIONS.ROOT, PAYMENT_FORM_IMAGES.UI_ICON_CREDITCARD),
  affirm: getAssetsUrl(ASSETS_LOCATIONS.CONTENT, PAYMENT_FORM_IMAGES.AFFIRM_LOGO),
  paypal: 'https://www.paypalobjects.com/webstatic/en_US/i/buttons/PP_logo_h_100x26.png',
  applePay: getAssetsUrl(ASSETS_LOCATIONS.CONTENT, PAYMENT_FORM_IMAGES.APPLE_PAY_LOGO),
  googlePay: getAssetsUrl(ASSETS_LOCATIONS.CONTENT, PAYMENT_FORM_IMAGES.GOOGLE_PAY_LOGO),
  shift4: 'https://www.shift4.com/wp-content/uploads/2019/05/shift4-logo.png'
};

/**
 * Payment methods configuration objects to be used to return the payment type
 * on the paload.
 * @enum {Object}
 */
const paymentMethododsConf = {
  'credit-card': {
    type: paymentMethods.CREDIT_CARD,
    label: 'creditCard'
  },
  affirm: {
    type: paymentMethods.AFFIRM,
    label: 'affirm'
  },
  applePayEnabled: {
    type: paymentMethods.APPLE_PAY,
    label: 'digitalWallet'
  },
  googlePayEnabled: {
    type: paymentMethods.GOOGLE_PAY,
    label: 'digitalWallet'
  },
  paypal: {
    type: paymentMethods.PAYPAL,
    label: 'paypal'
  },
  shift4: {
    type: paymentMethods.SHIFT4,
    label: 'ALTERNATIVE'
  }
};

/**
 * Type of credit cards
 * @enum {string}
 */
const creditCardTypes = {
  visa: 'VS',
  mastercard: 'MC',
  'american-express': 'AX',
  discover: 'DS',
  default: 'any'
};

const CREDIT_CARD_TEST_NUMBER = '4444333322221111';
const NEXT_GEN_QA = 'NextGenQA';
const APPLE_PAY_TYPE = 'applePay';
const GOOGLE_PAY_TYPE = 'googlePay';
export const DEFAULT_RATE_CODE = 'AMTCERT';

/**
 * Formik initial values
 */
const getInitialValues = isSeasonsFlow => ({
  contactInfo: {
    phone: '',
    firstName: '',
    lastName: '',
    email: '',
    textAgreement: false,
    ...(isSeasonsFlow ? { secondary: null } : {})
  },
  billingInfo: {
    billingAddress: '',
    postalCode: '',
    city: '',
    state: 'AL',
    country: SUPPORTED_COUNTRIES.UnitedStates
  },
  paymentOptions: {
    selected: 'credit-card',
    ccNameOnCard: '',
    ccMonth: '',
    ccYear: '',
    ccCvv: '',
    ccCardNumber: '',
    paypal: {
      nonce: '',
      firstName: '',
      lastName: '',
      email: '',
      deviceId: ''
    },
    googlePay: {
      tokenizationData: {},
      type: '',
      info: {},
      description: ''
    },
    applePay: {
      paymentData: {},
      paymentMethod: {},
      transactionIdentifier: ''
    }
  },
  specialRequests: [],
  specialRequestsOther: '',
  specialOccasion: [],
  specialOccasionOther: '',
  specialOccasionBirthday: '',
  isCASLMarked: false
});

/**
 * Based on a credit card type, return the correspondant code. On dev
 * environment pass the test code "1111222233334444" as a visa card.
 * @param {number} cardNumber Credit card number.
 * @return {string} Credit card code.
 */
export const getCardCode = cardNumber => {
  if (cardNumber === CREDIT_CARD_TEST_NUMBER) {
    return creditCardTypes.visa;
  }

  const cardType = creditCardType(cardNumber);
  const [bestCardMatch] = cardType;

  if (!bestCardMatch) return creditCardTypes.default;

  const { type: bestCardType } = bestCardMatch;
  return creditCardTypes[bestCardType] || creditCardTypes.default;
};

/**
 *
 * @param {*} formData
 * @param {*} stateData
 */
const getAffirmCheckoutPayload = (formData, stateData) => {
  const { contactInfo, billingInfo } = formData;
  const {
    selectedSuite,
    checkinDateSelection,
    checkoutDateSelection,
    resortLocation,
    selectedPackages,
    summaryTotal,
    summaryTaxes
  } = stateData;
  const displayName = `${COPY.GREAT_WOLF_LODGE} ${resortLocation} ; ${resortLocation}, ${COPY.US}; ${checkinDateSelection}-${checkoutDateSelection}; ${selectedSuite.title}`;

  const billingInformation = {
    name: {
      first: contactInfo.firstName,
      last: contactInfo.lastName
    },
    address: {
      line1: billingInfo.billingAddress,
      line2: billingInfo.addressLineTwo || '',
      city: billingInfo.city,
      state: billingInfo.state,
      zipcode: billingInfo.postalCode,
      country: billingInfo.country
    },
    email: contactInfo.email
  };

  const filteredSelectedPackages = selectedPackages.filter(
    filteredPackage => filteredPackage.isAdjustment === undefined
  );

  return {
    metadata: {
      mode: 'modal',
      entity_name: resortLocation
    },
    merchant: {
      user_confirmation_url: `${window.location.origin}/plan/payment`,
      user_cancel_url: `${window.location.origin}/plan/payment`,
      user_confirmation_url_action: 'GET',
      name: 'Great Wolf Lodge'
    },
    billing: billingInformation,
    shipping: {
      ...billingInformation,
      phone_number: unmaskValue(contactInfo.phone)
    },
    items: [
      {
        display_name: displayName,
        sku: selectedSuite.suiteCode,
        unit_price: selectedSuite?.totalRate * 100,
        qty: 1,
        // Item image and item url are rendering a relative path but I think
        // should render an absolute path to those urls for affirm
        item_image_url: selectedSuite.mainImage,
        item_url: selectedSuite.anchorPath
      },
      ...filteredSelectedPackages.map(selectedPackage => {
        return {
          display_name: selectedPackage.packageName || get(selectedPackage, 'shortDescription', ''),
          sku: selectedPackage.packageCode,
          unit_price: Math.abs(selectedPackage.total.toFixed(2) * 100),
          qty: selectedPackage.quantity,
          item_image_url: '',
          item_url: ''
        };
      })
    ],
    shipping_amount: 0,
    tax_amount: parseInt(Math.round(summaryTaxes * 100)),
    total: parseInt(Math.round(summaryTotal * 100))
  };
};

/**
 * Returns and transform all payment details based on the type of payment
 * selected.
 * @param {Object} paymentData Data coming from the payment form
 * @param {Object} stateData Redux state data
 */
const getPaymentDetails = (paymentData, stateData, flowName) => {
  const {
    ccYear,
    ccMonth,
    ccCardNumber,
    ccNameOnCard,
    ccCvv,
    selected,
    cardCode,
    vaultedCardId,
    isI4IframePayment,
    security,
    token
  } = paymentData;
  const {
    resortLocation,
    summaryTotal,
    checkoutToken,
    paypal,
    googlePay,
    applePay,
    profile,
    offer,
    suiteRates,
    showLoyalty
  } = stateData;
  const expiration = ccYear && ccMonth ? GwDatesWrapper.format(`${ccYear}-${ccMonth}-05`) : null;
  const { isSuiteBookingFlow, isDaypassBookingFlow } = determineWhichFlow(flowName);
  const isSuiteOrDaypass = isSuiteBookingFlow || isDaypassBookingFlow;
  const isApplePay = selected === 'applePayEnabled';
  let paymentObject = {};
  if (selected === 'credit-card') {
    if (isSuiteOrDaypass) {
      paymentObject = {
        number: ccCardNumber,
        cidNumber: ccCvv,
        holder: ccNameOnCard,
        expirationYear: ccYear,
        expirationMonth: ccMonth,
        type: cardCode ?? getCardCode(ccCardNumber)
      };
    } else {
      paymentObject = {
        number: ccCardNumber,
        holder: ccNameOnCard,
        ...(expiration && { expiration }),
        ...(vaultedCardId && { vaultedCardId }),
        securityCode: 0,
        cardCode: cardCode ?? getCardCode(ccCardNumber),
        cidNumber: ccCvv,
        ccYear: ccYear,
        ccMonth: ccMonth
      };
    }

    if (isI4IframePayment) {
      paymentObject = {
        ...paymentObject,
        ...(security && { security }),
        ...(token && { token })
      };
    }
  } else if (selected === 'affirm') {
    if (isSuiteOrDaypass) {
      paymentObject = {
        token: checkoutToken
      };
    } else {
      paymentObject = {
        checkoutToken: checkoutToken,
        property: resortLocation,
        amount: summaryTotal,
        reservationId: null
      };
    }
  } else if (selected === 'paypal') {
    paymentObject = {
      nonce: paypal.nonce,
      firstName: paypal.firstName,
      lastName: paypal.lastName,
      email: paypal.email,
      deviceId: paypal.deviceId
    };
  } else if (selected === 'googlePayEnabled' || isApplePay) {
    const billingContactData = isApplePay ? JSON.parse(applePay.i4go_applepaytoken) : null;
    if (isSuiteOrDaypass) {
      paymentObject = {
        name: isApplePay ? COPY.applePay : COPY.googlePay,
        type: googlePay?.i4go_cardtype ?? applePay?.i4go_cardtype,
        extendedCardData: googlePay?.i4go_extendedcarddata ?? applePay?.i4go_extendedcarddata,
        holder:
          googlePay?.i4go_cardholdername ??
          applePay?.i4go_cardholdername ??
          `${billingContactData?.billingContact?.givenName} ${billingContactData?.billingContact?.familyName}`,
        expirationMonth: googlePay?.i4go_expirationmonth ?? applePay?.i4go_expirationmonth,
        expirationYear: googlePay?.i4go_expirationyear ?? applePay?.i4go_expirationyear,
        token: googlePay?.i4go_uniqueid ?? applePay?.i4go_uniqueid
      };
    } else {
      paymentObject = {
        name: isApplePay ? COPY.applePay : COPY.googlePay,
        cardCode: googlePay?.i4go_cardtype ?? applePay?.i4go_cardtype,
        extendedCardData: googlePay?.i4go_extendedcarddata ?? applePay?.i4go_extendedcarddata,
        holder:
          googlePay?.i4go_cardholdername ??
          applePay?.i4go_cardholdername ??
          `${billingContactData?.billingContact?.givenName} ${billingContactData?.billingContact?.familyName}`,
        expirationMonth: googlePay?.i4go_expirationmonth ?? applePay?.i4go_expirationmonth,
        expirationYear: googlePay?.i4go_expirationyear ?? applePay?.i4go_expirationyear,
        token: googlePay?.i4go_uniqueid ?? applePay?.i4go_uniqueid,
        lastFourDigits: googlePay?.otn?.cardnumber?.slice(12) ?? applePay?.otn?.cardnumber?.slice(12)
      };
    }
  }

  const handlePoints = () => {
    const isSeasonsFlow = window.location.pathname.includes(routes.seasonpass.self);
    if (isSeasonsFlow) {
      return 0;
    }
    if (showLoyalty && profile && profile.user && profile.user.pointsBalance) {
      if (profile.user.pointsBalance <= suiteRates[0].totalNightlyRate) {
        return profile.user.pointsBalance.toFixed();
      } else if (profile.user.pointsBalance > suiteRates[0].totalNightlyRate) {
        return suiteRates[0].totalNightlyRate.toFixed();
      }
    } else {
      return 0;
    }
  };

  if (isSuiteOrDaypass) {
    return {
      type: paymentMethododsConf[selected].type,
      [paymentMethododsConf[selected].label]: paymentObject,
      loyalty: {
        points: flowName !== FLOW_NAMES.BIRTHDAY ? Number(handlePoints()) ?? 0 : 0,
        offers: flowName !== FLOW_NAMES.BIRTHDAY && offer && offer.activeOffers ? offer.activeOffers : []
      },
      isI4IframePayment
    };
  }

  return {
    type: paymentMethododsConf[selected].type,
    [paymentMethododsConf[selected].label]: paymentObject,
    shouldChargePackagesUpfront: false,
    points: flowName !== FLOW_NAMES.BIRTHDAY ? Number(handlePoints()) ?? 0 : 0,
    offers: flowName !== FLOW_NAMES.BIRTHDAY && offer && offer.activeOffers ? offer.activeOffers : [],
    isI4IframePayment
  };
};

/**
 * Returns a formed object containning address details.
 * @param {Object} billingData Payment form billing data
 * @param {Object} contactData Payment form contact data
 */
const getAddressDetails = (billingData, contactData) => {
  const { billingAddress, addressLineTwo, city, state, country, postalCode } = billingData;
  const { email } = contactData;
  return {
    street1: billingAddress,
    street2: addressLineTwo || '',
    city: city,
    state: state,
    country: country,
    postalCode: postalCode,
    email: email
  };
};

/**
 * Return customer details
 * @param {Object} contactInfo User data
 */
const getCustomerDetails = (contactInfo, isSeasonsFlow) => {
  const contactInfoReturn = {
    gender: 1,
    firstName: contactInfo.firstName,
    lastName: isDevEnvironment ? NEXT_GEN_QA : contactInfo.lastName,
    email: contactInfo.email,
    phoneNumber: maskPhone(contactInfo.phone),
    canContactBySms: contactInfo.textAgreement,
    isCASLMarked: contactInfo.isCASLMarked,
    isPhoneUpdated: contactInfo?.isPhoneUpdated ?? false
  };
  //For the season pass payload second contact optional form
  //====================================================
  if (isSeasonsFlow && contactInfo?.secondary) {
    contactInfoReturn.secondary = contactInfo.secondary;
  }
  return contactInfoReturn;
};

/**
 * Returns the payment info returned by Paypal checkout
 * @param {Object} paypal Paypal success object
 */
const getPaypalPaymentInfo = paypal => {
  return {
    nonce: paypal.nonce,
    firstName: paypal.details.firstName,
    lastName: paypal.details.lastName,
    email: paypal.details.email,
    deviceId: paypal.deviceId
  };
};

/**
 * Returns the billing info selected on Paypal site
 * @param {Object} paypal Paypal success object
 */
const getPaypalBillingInfo = paypal => {
  return {
    billingAddress: paypal.details.billingAddress.line1 || '',
    postalCode: paypal.details.billingAddress.postalCode || '',
    city: paypal.details.billingAddress.city || '',
    state: paypal.details.billingAddress.state || '',
    country: paypal.details.billingAddress.countryCode || ''
  };
};

/**
 * Returns the payment info returned by Google Pay checkout
 * @param gPay
 * @returns {{country: (*|string), city: (string|string), postalCode: (*|string), billingAddress: (string|string), state: (string|string)}}
 */
const getGooglePayBillingInfo = gPay => {
  return {
    billingAddress: gPay?.info?.billingAddress.address1 || '',
    postalCode: gPay?.info?.billingAddress.postalCode || '',
    city: gPay?.info?.billingAddress.locality || '',
    state: gPay?.info?.billingAddress.administrativeArea || '',
    country: gPay?.info?.billingAddress.countryCode || ''
  };
};

/**
 * Returns the payment info returned by Apple Pay checkout
 * @param applePayBillingContact
 * @returns {{country: (*|string), city: (string|string), postalCode: (*|string), billingAddress: (string|string), state: (string|string)}}
 */
const getApplePayBillingInfo = applePayBillingContact => {
  return {
    billingAddress: applePayBillingContact?.addressLines[0] || '',
    postalCode: applePayBillingContact?.postalCode || '',
    city: applePayBillingContact?.locality || '',
    state: applePayBillingContact?.administrativeArea || '',
    country: applePayBillingContact?.countryCode || ''
  };
};

/**
 * Returns a contact and billing info based on user logged in for formik.
 * @param {Object} profile Payment form data
 * @param {string} resortLocation
 */
const setContactandBillingAddressInfo = (profile, resortLocation, isSeasonsFlow) => {
  let contactAndBillingAddressInfo = { ...getInitialValues(isSeasonsFlow) };

  if (profile) {
    contactAndBillingAddressInfo.contactInfo = {
      phone: profile.phoneNumber ? maskPhone(profile.phoneNumber) || '' : '',
      firstName: profile.firstName || '',
      lastName: profile.lastName || '',
      email: profile.email || '',
      textAgreement: resortLocation !== SITE_NIAGON,
      ...(isSeasonsFlow ? { secondary: null } : {})
    };
    contactAndBillingAddressInfo.billingInfo = {
      billingAddress: profile.streetLine1 || '',
      addressLineTwo: profile.streetLine2 || '',
      postalCode: profile.zip || '',
      city: profile.city || '',
      state: profile.stateProvince || '',
      country: profile.country || SUPPORTED_COUNTRIES.UnitedStates
    };
  }
  return contactAndBillingAddressInfo;
};

const getPartyOrder = state => {
  const {
    partyDate,
    partyAdults,
    partyKids,
    partyInfants,
    partySpectators,
    partyTime,
    bdaySelectedParty,
    guestsOfHonor,
    selectedPackages
  } = state;
  const timeDate = bdaySelectedParty.priceDetails.times.find(timeData => timeData.name === partyTime);
  const BIRTHDAY_PAYMENT_INDEX = {
    0: 'pizzaPackages',
    1: 'drinksPackages',
    2: 'cakePackages',
    3: 'addOnPackages'
  };

  function createArrayBirthdayPackages(selectedPackages) {
    let packages = {};

    const groupedPackages = {};
    selectedPackages.forEach(pkg => {
      const summarySectionType = pkg.summarySectionType;
      if (groupedPackages[summarySectionType]) {
        groupedPackages[summarySectionType].push(pkg);
      } else {
        groupedPackages[summarySectionType] = [pkg];
      }
    });

    Object.keys(groupedPackages).forEach(gKey => {
      // packageCodes
      const packageCodes = [];
      const groupedPackages = Object.groupBy(selectedPackages, ({ summarySectionType }) => summarySectionType);
      const sortedPackage = groupedPackages[gKey]?.sort((a, b) => {
        // If packageCode is the same, compare isExtra (false comes before true)
        return a.isExtra - b.isExtra;
      });

      if (+gKey === 3) {
        sortedPackage?.forEach(pkg => {
          packageCodes.push({
            code: pkg.packageCode,
            quantity: pkg.quantity
          });
        });
      } else {
        sortedPackage.forEach(pkg => {
          const qty = pkg.quantity;
          const code = pkg.packageCode;
          for (let i = 0; i < qty; i++) {
            packageCodes.push(code);
          }
        });
      }

      packages[BIRTHDAY_PAYMENT_INDEX[gKey]] = packageCodes;
    });

    return packages;
  }

  const extraPackages = selectedPackages.filter(pkg => pkg.isExtra === true);

  return {
    party: {
      guestsOfHonor: guestsOfHonor,
      partyDate: partyDate,
      partySize: {
        adults: partyAdults,
        kids: partyKids,
        infants: partyInfants,
        spectators: partySpectators
      },
      partyTime: {
        name: timeDate.name,
        startTime: timeDate.startTime,
        endTime: timeDate.endTime
      },
      partyType: bdaySelectedParty.partyType,
      depositAmount: +(
        +bdaySelectedParty.priceDetails?.depositAmount +
        (bdaySelectedParty.priceDetails.remainingBalance !== 0 ? 0 : handlePackagesTotalWithTax(extraPackages))
      ).toFixed(2),
      remainingBalance: +(bdaySelectedParty.priceDetails.remainingBalance > 0
        ? bdaySelectedParty.priceDetails?.remainingBalance + handlePackagesTotalWithTax(extraPackages)
        : 0
      ).toFixed(2),
      ...createArrayBirthdayPackages(selectedPackages)
    }
  };
};

/**
 * Returns a well formed payload to be used by createReservation.
 * @param {Object} formValues Payment form data
 * @param {Object} stateValues Redux state data
 */
const getPayload = async (flowName, formValues, stateValues, callBack = () => {}) => {
  const { isPartyBookingFlow, isSuiteBookingFlow, isDaypassBookingFlow } = determineWhichFlow(flowName);
  const isSeasonsFlow = window.location.pathname.includes('/seasonpass');
  const { paymentOptions, billingInfo, contactInfo } = formValues;
  const { profile } = stateValues;

  const defaultPayload = {
    profileId: profile.user ? profile.user.id : null,
    address: getAddressDetails(billingInfo, contactInfo),
    payment: getPaymentDetails(paymentOptions, stateValues, flowName),
    customer: getCustomerDetails(contactInfo, isSeasonsFlow)
  };

  let payload = {};

  if (isSuiteBookingFlow || isDaypassBookingFlow || isSeasonsFlow) {
    const isDayPassOrSeasonsFlow = isDaypassBookingFlow || isSeasonsFlow;
    const isFreeVoucher = isDaypassBookingFlow && stateValues.dayPasses?.selectedRate === 0;
    const addressInfo = { ...defaultPayload.address };
    delete addressInfo.email;
    payload = {
      arrival: isDayPassOrSeasonsFlow ? stateValues.dayPasses.arrivalDate : stateValues.checkinDateSelection,
      departure: isDayPassOrSeasonsFlow ? stateValues.dayPasses.departureDate : stateValues.checkoutDateSelection,
      totalGuests: {
        adults: stateValues.adultsCount,
        children: stateValues.kidsAges
      },
      lodge: {
        code: stateValues.resortLocation
      },
      product: {
        roomCode: isDayPassOrSeasonsFlow
          ? stateValues.dayPasses.selectedItemCode || stateValues.dayPasses.passType
          : stateValues.selectedSuite.suiteCode,
        price: {
          rateCode: isDayPassOrSeasonsFlow
            ? stateValues.dayPasses.selectedRateCode
            : stateValues.selectedSuite.rateCode,
          promoCode: isDayPassOrSeasonsFlow ? stateValues.dayPasses.promoCode : stateValues.validOfferCode,
          qualifyingId: isDayPassOrSeasonsFlow ? stateValues.dayPasses.qualifyingId : stateValues.qualifyingID,
          availabilityKey: isDayPassOrSeasonsFlow
            ? stateValues.dayPasses.availabilityKey
            : stateValues.selectedSuite.availabilityKey
        }
      },
      addOns: addOnsPaymentFormat(stateValues.selectedPackages),
      contactInfo: getCustomerDetails(contactInfo, isSeasonsFlow),
      expectedRates: expectedRatesPaymentFormat(stateValues.suiteRates)
    };
    // For free vouchers, exclude payment and billing, add isFreeVoucher flag
    if (isFreeVoucher) {
      payload.isFreeVoucher = true;
    } else {
      payload.payment = { ...{ billingAddress: addressInfo }, ...defaultPayload.payment };
    }

    if (isDayPassOrSeasonsFlow) {
      payload.date = stateValues?.dayPasses?.arrivalDate;
      payload.numberOfPasses = stateValues?.dayPasses?.numberOfGuests;
    }
    if (isSeasonsFlow) {
      if (payload.payment?.type === paymentMethods.CREDIT_CARD && payload.isTokenisationEnabled) {
        await handleVaultedCardDetails(payload.payment, payload.lodge.code, {
          deleteCardDetails: paymentOptions.isI4IframePayment
        });
      }
      payload.date = stateValues?.dayPasses?.departureDate;
    }
    if (isDaypassBookingFlow) {
      delete payload.arrival;
      delete payload.departure;
      delete payload.totalGuests;
    }
    try {
      // For free vouchers, skip payment validation
      if (isFreeVoucher) {
        return payload;
      }
      const cidNumberIsRequired = !paymentOptions.isI4IframePayment;
      return await createTiogasReservationSchema(cidNumberIsRequired).validate(payload);
    } catch (error) {
      callBack(error?.errors[0] ?? 'Something Went Wrong');
      console.error(error);
      return;
      // null catch statement to prevent error from breaking the app
    }
  }

  if (isPartyBookingFlow) {
    const birthdayPayload = getPartyOrder(stateValues);
    payload = { ...defaultPayload, ...birthdayPayload };
    try {
      return await partyReservationSchema.validate(payload);
    } catch (error) {
      callBack(error?.errors[0] ?? 'Something Went Wrong');
      console.error(error);
      return;
      // null catch statement to prevent error from breaking the app
    }
  }
  return payload;
};

const addOnsPaymentFormat = addOns => {
  if (isArrayEmpty(addOns)) return [];
  return addOns.map(addOn => {
    return {
      code: addOn.packageCode,
      quantity: addOn.quantity,
      availabilityKey: addOn.availabilityKey,
      date: addOn.startDate
    };
  });
};

const expectedRatesPaymentFormat = expectedRates => {
  if (isArrayEmpty(expectedRates)) return [];
  return expectedRates.map(eRate => {
    return {
      date: eRate.date,
      suiteCode: eRate.suiteCode,
      guaranteeType: eRate.guaranteeType,
      totalNightlyRate: eRate.totalNightlyRate,
      advancedDeposit: eRate.advancedDeposit,
      taxesAndFees: eRate.taxesAndFees
    };
  });
};

/**
 * @function
 * Check if the all contact and billing information is complete before we
 * render the info text component
 * @param {Object} contactInfo Formik data with the contactInfo
 * @param {Object} billingInfo Formik data with the billingInfo
 * @Return {Boolean} If contactInfo and billingInfo haven't any null or empty properties then the result will be true
 */
const allFieldsProvided = contactInfo => {
  if (!contactInfo) return false;

  return checkObjectProperties(contactInfo);
};

/**
 * @function
 * Returns requited data to render payment tabs depending on calculated values
 * @param {Boolean} isPlanPage
 * @returns
 */
const getPaymentMethods = (isPlanPage, resortLocation) => {
  const { CREDIT_CARD, AFFIRM, PAYPAL, SHIFT4, APPLE_PAY, GOOGLE_PAY } = paymentMethodsTypes;

  const methods = {
    [CREDIT_CARD]: {
      isEnabled: true,
      isEnabledOutsidePlanPage: true,
      order: 1
    },
    [AFFIRM]: {
      isEnabled: isLocationAvailableWithAffirm(resortLocation),
      isEnabledOutsidePlanPage: false,
      order: 4
    },
    [PAYPAL]: {
      isEnabled: isPayPalEnabled(process.env.REACT_APP_PAYPAL_ENABLED_AT, resortLocation),
      isEnabledOutsidePlanPage: true,
      order: 5
    },
    [SHIFT4]: {
      isEnabled: process.env.REACT_APP_ENABLE_SHIFT4 === 'true',
      isEnabledOutsidePlanPage: true,
      order: 6
    },
    [APPLE_PAY]: {
      isEnabled: true,
      isEnabledOutsidePlanPage: true,
      order: 7
    },
    [GOOGLE_PAY]: {
      isEnabled: true,
      isEnabledOutsidePlanPage: true,
      order: 8
    }
  };

  const availablePaymentMethods = Object.values(paymentMethodsTypes).filter(method => {
    return isPlanPage
      ? methods[method]?.isEnabled
      : methods[method]?.isEnabled && methods[method]?.isEnabledOutsidePlanPage;
  });

  let availableSpace = 100;

  return availablePaymentMethods
    .map((method, idx) => {
      const { length } = availablePaymentMethods;
      if (availableSpace % length === 0 || idx !== 0) {
        return { name: method, width: `${availableSpace / length}%` };
      }

      const firstTabSize = Math.ceil(availableSpace / length / 10) * 10;
      availableSpace -= firstTabSize;
      return { name: method, width: `${firstTabSize}%` };
    })
    .sort((a, b) => a.order < b.order);
};

const fetchShift4WalletConfig = async (accessBlock, i4GoServerUrl) => {
  try {
    const payApiClient = new PaymentApiClient();
    const { data } = await payApiClient.getShift4WalletConfig(accessBlock, i4GoServerUrl);
    return data;
  } catch (e) {
    console.log(e);
  }
};

const approveShift4WalletTransaction = async (accessBlock, token, paymentType, i4GoServerUrl) => {
  try {
    const applePayToken = paymentType === APPLE_PAY_TYPE ? token : '';
    const googlePayToken = paymentType === GOOGLE_PAY_TYPE ? token : '';
    const payApiClient = new PaymentApiClient();
    const { data } = await payApiClient.approveShift4WalletTransaction(
      accessBlock,
      applePayToken,
      googlePayToken,
      i4GoServerUrl
    );
    return data;
  } catch (e) {
    console.log(e);
  }
};

export const fetchTiogaTokenAccessBlock = async (resortLocation, paymentType, totalToCharge, origin = null) => {
  try {
    const payApiClient = new RoomBookingApiClient();
    return await payApiClient.getShift4i4AccessBlockTioga(resortLocation, paymentType, totalToCharge, origin);
  } catch (e) {
    console.error(e);
  }
};

/**
 * Create shift4i4Go vaulted card details
 *
 * @function
 * @param {number, ccMonth, ccYear, cidNumber, holder} creditCardData credt Card data to be tokenized
 * @param {postalCode, street1} billingAddressData billing address data to be tokenized
 */
const fillVaultedCardDetails = async (creditCardData, billingAddressData, resortLocation) => {
  const { postalCode, street1 } = billingAddressData;
  const cardTokenAccessBlockData = await fetchTiogaTokenAccessBlock(resortLocation, 1, 0);
  const ccStreetAddress = street1.replace(/(^\d+)(.+$)/i, '$1');
  const data = {
    i4go_accessblock: cardTokenAccessBlockData.data.token,
    i4go_cardnumber: creditCardData?.number,
    i4go_expirationmonth: creditCardData?.ccMonth ?? creditCardData?.expirationMonth,
    i4go_expirationyear: creditCardData?.ccYear ?? creditCardData?.expirationYear,
    i4go_cvv2code: creditCardData?.cidNumber,
    i4go_cardholdername: creditCardData?.holder ?? creditCardData?.holder,
    i4go_postalcode: postalCode,
    i4go_streetaddress: ccStreetAddress
  };
  const vaultedCardDetails = (
    await new Shift4i4GoApiClient().getVaultedCardDetails(cardTokenAccessBlockData.data.url, data)
  ).data;

  return {
    vaultedCardId: vaultedCardDetails.i4go_uniqueid,
    lastFourDigits: vaultedCardDetails.otn.cardnumber.replaceAll('X', '').trim(),
    cardnumber: vaultedCardDetails.otn.cardnumber,
    cidNumber: '',
    number: vaultedCardDetails.otn.cardnumber
  };
};

export const transformPaymentDataToOptions = (paymentMethodsComponents, optionsVariation) =>
  optionsVariation.reduce((paymentMethods, currentOption) => {
    if (!paymentMethodsComponents[currentOption].show || !paymentMethodsComponents[currentOption].allowedOnLocation)
      return paymentMethods;

    return [
      ...paymentMethods,
      {
        paymentMethod: {
          uuid: currentOption,
          ...paymentMethodsComponents[currentOption]
        }
      }
    ];
  }, []);

export const getSelectedPaymentMethod = (
  appleGooglePayMethodsFirst,
  isGooglePaymentActive,
  availablePaymentMethods
) => {
  if (!appleGooglePayMethodsFirst) return paymentMethodsTypes.CREDIT_CARD;
  if (window.ApplePaySession && availablePaymentMethods.includes(paymentMethodsTypes.APPLE_PAY))
    return paymentMethodsTypes.APPLE_PAY;
  if (isGooglePaymentActive && availablePaymentMethods.includes(paymentMethodsTypes.GOOGLE_PAY))
    return paymentMethodsTypes.GOOGLE_PAY;
  return paymentMethodsTypes.CREDIT_CARD;
};

export const setBasicBillingInfo = (values, addressInfo) => {
  values.billingInfo.city = addressInfo.locality;
  values.billingInfo.postalCode = addressInfo.postalCode;
  values.billingInfo.country = addressInfo.countryCode;
  values.billingInfo.state = addressInfo.administrativeArea;
};

/**
 * Handles vaulted card details for payment data
 * @param {Object} paymentData - Payment object containing creditCard, billing address, and tokenisation info
 * @param {string} lodgeCode - Resort location code
 * @param {Object} options - Optional configuration
 * @param {boolean} options.deleteCardDetails - Whether to delete card number and CVV (default: true)
 * @returns {Promise<void>}
 */
export const handleVaultedCardDetails = async (paymentData, lodgeCode, options = {}) => {
  const { deleteCardDetails = true } = options;

  const vaultedCardDetails = await fillVaultedCardDetails(
    paymentData.creditCard,
    paymentData.billingAddress,
    lodgeCode
  );

  paymentData.creditCard.token = vaultedCardDetails.vaultedCardId;

  if (deleteCardDetails) {
    delete paymentData.creditCard.cidNumber;
    delete paymentData.creditCard.number;
  }

  delete paymentData.isTokenisationEnabled;
};

export {
    allFieldsProvided,
    approveShift4WalletTransaction,
    fetchShift4WalletConfig,
    fillVaultedCardDetails,
    getAffirmCheckoutPayload,
    getApplePayBillingInfo,
    getGooglePayBillingInfo,
    getPayload,
    getPaymentMethods,
    getPaypalBillingInfo,
    getPaypalPaymentInfo,
    paymentFormLogos,
    setContactandBillingAddressInfo
};

